import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { MdKeyboardArrowRight } from "react-icons/md";
import { FaRegBuilding } from "react-icons/fa";
import imgdep from "../../Assets/ece11.jpg";
import SummaryApi from "../../common";
import ModalDepartment from "./ModalDepaartment";

const getDeptImage = (imagePath) => {
  if (!imagePath) return imgdep;

  const imagePathWithoutPrefix = imagePath.startsWith("images/")
    ? imagePath.slice(7)
    : imagePath;

  const normalizedPath = imagePathWithoutPrefix.replace(/\\/g, "/");

  const imageUrl = `${SummaryApi.getdeptImages.url}${normalizedPath}`;

  return imageUrl;
};

const AllDepartments = () => {
  const [departments, setDepartments] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const departmentsPerPage = 3;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editDepartment, setEditDepartment] = useState(null);

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await fetch(SummaryApi.getAllDepartment.url, {
          method: "GET",
        });
        const result = await response.json();

        if (result.status) {
          const fetchedDepartments = result.data.map((department) => ({
            id: department.department_id,
            name: department.department_name,
            head: department.department_head,
            building: department.building,
            office: department.office_number,
            email: department.email,
            budget: department.budget,
            description: department.description,
            image: department.department_image
              ? getDeptImage(department.department_image)
              : imgdep,
          }));
          setDepartments(fetchedDepartments);
        } else {
          console.error("Failed to fetch departments:", result.message);
        }
      } catch (error) {
        console.error("Error fetching departments:", error);
      }
    };

    fetchDepartments();
  }, []);

  const indexOfLastDepartment = currentPage * departmentsPerPage;
  const indexOfFirstDepartment = indexOfLastDepartment - departmentsPerPage;
  const currentDepartments = departments.slice(
    indexOfFirstDepartment,
    indexOfLastDepartment
  );

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const totalPages = Math.ceil(departments.length / departmentsPerPage);

  const handleEdit = (department) => {
    console.log("Editing department:", department); // Make sure `department.id` is present
    if (!department.id) {
      alert("Error: Selected department has no ID!");
      return;
    }
    setEditDepartment(department);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditDepartment((prev) => ({ ...prev, [name]: value }));
  };

  // Handle saving changes to the department
  const handleSave = async () => {
    try {
      // Send the updated department data to the API
      const response = await fetch(SummaryApi.updateDepartment.url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          department_id: editDepartment.id,
          name: editDepartment.name,
          head: editDepartment.head,
          building: editDepartment.building,
          office: editDepartment.office,
          budget: editDepartment.budget,
          description: editDepartment.description,
          email: editDepartment.email,
          image: editDepartment.image,
        }),
      });

      // Check if the response is successful
      if (!response.ok) {
        throw new Error("Failed to update department");
      }

      // Handle the API response
      const result = await response.json();

      if (result.status) {
        // Find the updated department from the API response
        const updatedDepartment = result.data.find(
          (dept) => dept.department_id === editDepartment.id
        );

        if (updatedDepartment) {
          // Update the local state with the updated department
          setDepartments((prevDepartments) =>
            prevDepartments.map((department) =>
              department.department_id === updatedDepartment.department_id
                ? updatedDepartment
                : department
            )
          );
          // Close the modal after successful update
          closeModal();
          console.log("Department updated:", updatedDepartment);
        } else {
          console.error("Department not found in response");
        }
      } else {
        console.error("Error updating department:", result.message);
      }
    } catch (error) {
      console.error("Error updating department:", error);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditDepartment((prevState) => ({
          ...prevState,
          image: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="container">
      <h1 className="text-2xl sm:text-3xl md:text-3xl lg:text-3xl xl:text-3xl font-semibold mb-4 sm:mb-5 md:mb-6 text-center sm:text-left">
        Departments
      </h1>

      <nav className="text-gray-600 mb-4">
        <ol className="flex flex-wrap items-center space-x-2 text-sm sm:text-base md:text-lg lg:text-xl xl:text-xl">
          <li>
            <Link to="/dashboard" className="hover:text-[#ffa404]">
              Home
            </Link>
          </li>
          <li className="text-[#ffa404]">
            <MdKeyboardArrowRight size={25} />
          </li>
          <li className="text-[#ffa404]">
            <Link to="/alldepartments" className="hover:text-[#ffa404]">
              All Departments
            </Link>
          </li>
        </ol>
      </nav>

      <div className="bg-white shadow-md rounded-lg p-6 mt-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4 text-center sm:text-left">
          All College Departments
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {currentDepartments.map((department) => (
            <div
              key={department.id}
              className="bg-gray-50 border rounded-lg p-4"
            >
              <div className="flex row gap-1 mb-3">
                <img
                  src={department.image}
                  alt={department.name}
                  className="w-24 h-24 rounded-[15px]"
                />
                <div className="p-3">
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">
                    {department.name}
                  </h3>
                  <p className="text-gray-600">
                    {department.description.length > 35
                      ? department.description.slice(0, 35) + "..."
                      : department.description}
                  </p>
                </div>
              </div>
              <div className="flex justify-between mb-2 p-2">
                <p className="text-gray-600">Head </p>
                <p className="text-[#000] text-[16px]">{department.head}</p>
              </div>
              <div className="flex justify-between mb-2 p-2">
                <p className="text-gray-600">Building </p>
                <p className="text-[#000] text-[16px]">{department.building}</p>
              </div>
              <div className="flex justify-between mb-2 p-2">
                <p className="text-gray-600">Office Number </p>
                <p className="text-[#000] text-[16px]">{department.office}</p>
              </div>
              <div className="flex justify-between mb-2 p-2">
                <p className="text-gray-600">Email </p>
                <p className="text-[#000] text-[16px]">
                  <a
                    href={`mailto:${department.email}`}
                    className="text-blue-600"
                  >
                    {department.email}
                  </a>
                </p>
              </div>
              <div className="flex justify-between mb-2 p-2">
                <p className="text-gray-600">Budget </p>
                <p className="text-[#000] text-[16px]">{department.budget}</p>
              </div>
                      
            </div>
          ))}
        </div>

        {/* Pagination */}
        <div className="flex justify-end mt-6">
          <button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
            className="bg-blue-500 text-white p-2 rounded-md disabled:opacity-50"
          >
            Prev
          </button>
          <span className="mx-2 text-gray-800">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="bg-blue-500 text-white p-2 rounded-md disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
      {isModalOpen && (
        <ModalDepartment
          editDepartment={editDepartment}
          handleInputChange={handleInputChange}
          handleSave={handleSave}
          closeModal={closeModal}
          handleImageChange={handleImageChange}
        />
      )}
    </div>
  );
};

export default AllDepartments;
